import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserPreferences } from '../contexts/UserPreferencesContext';
import { useAmbientBackground } from '../hooks/useAmbientBackground';
import { useNightModeStyles } from '../hooks/useNightModeStyles';
import { useOrbPosition } from '../hooks/useOrbPosition';
import { useGlobalCounter } from '../hooks/useGlobalCounter';
import StreamingRecommendation from './StreamingRecommendation';
import ActivityPills from './ActivityPills';
import '../styles/hourly-gradients.css';
import '../styles/atmospheric-noise.css';

const ConversationalHome = () => {
  const { 
    preferences, 
    signOut,
    user
  } = useUserPreferences();
  const navigate = useNavigate();
  const { gradientClass } = useAmbientBackground();
  const { getNightModeClass } = useNightModeStyles();
  const orbPosition = useOrbPosition();
  const { count: recommendationCount, increment } = useGlobalCounter('layer_recommendations');

  const [currentActivity, setCurrentActivity] = useState('walk');
  const [location, setLocation] = useState(null);
  const [coordinates, setCoordinates] = useState(null);

  // Auto-detect location on page load
  useEffect(() => {
    if (!coordinates) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coords = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          setCoordinates(coords);
          
          // Reverse geocode for location name
          fetch(`https://api.open-meteo.com/v1/forecast?latitude=${coords.latitude}&longitude=${coords.longitude}&current=temperature_2m&timezone=auto`)
            .then(response => response.json())
            .then(data => {
              if (data.timezone) {
                const city = data.timezone.split('/').pop()?.replace(/_/g, ' ');
                setLocation(city || 'Your location');
              }
            })
            .catch(() => setLocation('Your location'));
        },
        () => {
          // Fallback to user's home city if available
          if (preferences?.home_city) {
            setLocation(preferences.home_city);
          }
        }
      );
    }
  }, [coordinates, preferences]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  return (
    <div className={`min-h-screen bg-gray-50 dark:bg-gray-900 ${gradientClass} ${getNightModeClass()} font-inter relative`}>
      <div className="noise-overlay"></div>
      <div 
        className="orb-effect" 
        style={{
          top: `${orbPosition.top}%`,
          left: `${orbPosition.left}%`
        }}
      ></div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 py-8">
        {/* Header with Sign Out Button */}
        <div className="flex justify-between items-center mb-8">
          <div className="text-center flex-1">
            <h1 className="text-4xl font-light text-gray-800 dark:text-gray-200 mb-2">
              layers.today
            </h1>
            <p className="text-sm text-muted-foreground">
              {recommendationCount?.toLocaleString()} recommendations served
            </p>
          </div>
          
          {/* Sign Out Button */}
          {user && (
            <button
              onClick={handleSignOut}
              className="flex items-center px-4 py-2 bg-red-500 hover:bg-red-600 text-white text-sm rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg"
            >
              Sign Out
            </button>
          )}
        </div>

        {/* Activity Pills */}
        <ActivityPills 
          currentActivity={currentActivity}
          onActivityChange={setCurrentActivity}
        />

        {/* Main Recommendation */}
        <div className="mt-8">
          <StreamingRecommendation
            activity={currentActivity}
            location={location}
            coordinates={coordinates}
            userPreferences={preferences}
            onRecommendationShown={increment}
          />
        </div>
      </div>
    </div>
  );
};

export default ConversationalHome;